// apps/OtpAuthentication.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

export default function OtpAuthentication() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // États pour la gestion de l'OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [sessionValid, setSessionValid] = useState(true); // 🔥 NOUVEAU ÉTAT

  // Références pour les champs OTP
  const inputRefs = useRef([]);

  // 🔥 CORRECTION : Vérification de session au chargement
  useEffect(() => {
    const pendingLogin = JSON.parse(sessionStorage.getItem('pendingLogin') || '{}');
    const { email, password } = pendingLogin;
    
    
    if (!email || !password) {
      console.warn('❌ Session OTP expirée ou invalide');
      setSessionValid(false);
      setError('Session expirée. Redirection vers la page de connexion...');
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } else {
      
      setSessionValid(true);
    }
  }, [navigate]);

  // Récupérer les données utilisateur depuis la session (après vérification)
  const pendingLogin = JSON.parse(sessionStorage.getItem('pendingLogin') || '{}');
  const { email, password } = pendingLogin;

  // Configuration API
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Headers pour les requêtes API
  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  };

  // Countdown pour le renvoi d'OTP
  useEffect(() => {
    // 🔥 CORRECTION : Ne démarrer le countdown que si session valide
    if (!sessionValid) return;
    
    let timer;
    if (countdown > 0 && !canResend) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown, canResend, sessionValid]);

  // Gérer la saisie de l'OTP
  const handleOtpChange = (index, value) => {
    // N'autoriser que les chiffres
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Passer au champ suivant si un chiffre est saisi
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Gérer les touches du clavier
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      // Revenir au champ précédent
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Gérer le collage d'OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').slice(0, 6);
      const filledOtp = [...newOtp, ...Array(6 - newOtp.length).fill('')];
      setOtp(filledOtp);
      
      // Focus sur le dernier champ rempli
      const lastFilledIndex = newOtp.length - 1;
      if (lastFilledIndex < 5) {
        inputRefs.current[lastFilledIndex + 1]?.focus();
      }
    }
  };

  // Connexion avec OTP
  const performLoginWithOtp = async (email, password, otpString) => {
    try {
      const payload = {
        email: email,
        password: password,
        otp: otpString
      };

      
      const response = await axios.post(
        `${API_BASE_URL}/bo/login`,
        payload,
        {
          timeout: 15000,
          headers: getAuthHeaders(),
        }
      );

     
      if (response.data && response.data.access_token) {
     
        return response.data;
      } else {
        console.warn('❌ Réponse de connexion sans token');
        throw new Error('Token d\'accès non reçu');
      }

    } catch (error) {
      console.error('❌ Erreur connexion OTP:', error);
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          throw new Error('OTP invalide ou expiré');
        } else if (status === 422) {
          throw new Error('Données de connexion invalides');
        } else {
          throw new Error(data?.message || `Erreur ${status} lors de la connexion`);
        }
      }
      
      throw error;
    }
  };

  // Valider l'OTP avec connexion
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 🔥 CORRECTION : Vérifier d'abord la session
    if (!sessionValid) {
      setError('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      setError('Veuillez saisir les 6 chiffres du code OTP');
      return;
    }

    if (!email || !password) {
      setError('Session expirée. Veuillez vous reconnecter.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // APPEL API : Connexion avec OTP
      const loginData = await performLoginWithOtp(email, password, otpString);
      
    
      
      setSuccess('OTP vérifié avec succès ! Connexion en cours...');

      // SAUVEGARDE DES DONNÉES DANS LOCALSTORAGE
      localStorage.setItem("authToken", loginData.access_token);
      if (loginData.refresh_token) {
        localStorage.setItem("refreshToken", loginData.refresh_token);
      }
      if (loginData.user) {
        localStorage.setItem("userData", JSON.stringify(loginData.user));

      }
      if (loginData.business) {
       
        localStorage.setItem("businessData", JSON.stringify(loginData.business));
        
      }
      localStorage.setItem("tokenExpiry", new Date().getTime() + (loginData.expires_in * 1000));

      // NETTOYAGE DE LA SESSION
      sessionStorage.removeItem('pendingLogin');

     
      // Redirection vers le dashboard après succès
      setTimeout(() => {
        navigate('/dashboard/analytics', { 
          state: { 
            otpVerified: true,
            userData: loginData.user
          }
        });
      }, 1500);

    } catch (error) {
      console.error('❌ Erreur lors de la vérification OTP:', error);
      
      let errorMessage = error.message || "Erreur lors de la vérification OTP";
      
      if (error.message.includes('OTP invalide')) {
        errorMessage = "Code OTP incorrect ou expiré. Veuillez réessayer.";
      } else if (error.message.includes('Timeout')) {
        errorMessage = "Le serveur ne répond pas. Vérifiez votre connexion.";
      } else if (error.message.includes('Network Error')) {
        errorMessage = "Problème de connexion au serveur.";
      }
      
      setError(errorMessage);
      
      // Réinitialiser les champs en cas d'erreur
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer l'OTP
  const handleResendOtp = async () => {
    // 🔥 CORRECTION : Vérifier d'abord la session
    if (!sessionValid) {
      setError('Session expirée. Impossible de renvoyer l\'OTP.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Pour renvoyer l'OTP, on refait une vérification MFA qui déclenchera l'envoi
      const response = await axios.post(
        `${API_BASE_URL}/bo/login-mfa`,
        { 
          email: email,
          password: password
        },
        {
          timeout: 10000,
          headers: getAuthHeaders(),
        }
      );

      if (response.data && response.data.has_mfa === "YES") {
        setSuccess('Nouveau code OTP envoyé !');
        setOtp(['', '', '', '', '', '']);
        setCountdown(60);
        setCanResend(false);
        inputRefs.current[0]?.focus();
      } else {
        setError('Erreur lors de l\'envoi du code OTP');
      }
    } catch (error) {
      console.error('Erreur renvoi OTP:', error);
      let errorMessage = "Erreur lors de l'envoi du code OTP";
      
      if (error.response && error.response.status === 401) {
        errorMessage = "Identifiants incorrects. Session expirée.";
        setSessionValid(false);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Annuler et retourner à la page de connexion
  const handleCancel = () => {
    // Nettoyer la session
    sessionStorage.removeItem('pendingLogin');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/');
  };

  // 🔥 AFFICHAGE CONDITIONNEL : Masquer le formulaire si session invalide
  if (!sessionValid) {
    return (
      <div className="page-sign d-block py-5" style={{ 
        backgroundColor: '#f8f9fa', 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={5} md={7} sm={9}>
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="p-5">
                  <div className="mb-4">
                    <i className="bi bi-exclamation-triangle text-warning fs-1"></i>
                  </div>
                  <h4 className="fw-semibold text-dark mb-3">Session expirée</h4>
                  <p className="text-muted mb-4">
                    Votre session a expiré. Vous allez être redirigé vers la page de connexion.
                  </p>
                  <Spinner animation="border" variant="primary" />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-sign d-block py-5" style={{ 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={5} md={7} sm={9}>
            <Card className="border-0 shadow-sm">
              {/* En-tête */}
              <div className="bg-white border-bottom py-4 px-4 text-center">
                <div className="mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                    <i className="bi bi-shield-lock text-primary fs-3"></i>
                  </div>
                </div>
                <h4 className="fw-semibold text-dark mb-2">Vérification de sécurité</h4>
                <p className="text-muted mb-0">
                  Entrez le code de vérification envoyé sur votre mobile
                </p>
              </div>

              <Card.Body className="p-4">
                {/* Informations utilisateur */}
                {email && (
                  <div className="bg-light rounded p-3 mb-4 small text-center">
                    <div className="fw-bold text-primary mb-1">
                      {email}
                    </div>
                    <div className="text-muted">
                      Code OTP requis pour la connexion
                    </div>
                  </div>
                )}

                {/* Messages d'alerte */}
                {error && (
                  <Alert variant="danger" className="py-2 small">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" className="py-2 small">
                    <i className="bi bi-check-circle me-2"></i>
                    {success}
                  </Alert>
                )}

                {/* Formulaire OTP */}
                <Form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="form-label small text-muted mb-3">
                      Code de vérification à 6 chiffres
                    </label>
                    
                    <div className="d-flex justify-content-between gap-2">
                      {otp.map((digit, index) => (
                        <Form.Control
                          key={index}
                          ref={el => inputRefs.current[index] = el}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onPaste={handlePaste}
                          className="text-center fw-bold fs-5 py-3"
                          style={{ 
                            height: '60px',
                            fontSize: '1.25rem'
                          }}
                          disabled={loading}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={loading || otp.join('').length !== 6}
                      className="fw-semibold"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Vérification...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-shield-check me-2"></i>
                          Vérifier et se connecter
                        </>
                      )}
                    </Button>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancel}
                        disabled={loading}
                        size="sm"
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Retour connexion
                      </Button>

                      <Button
                        variant="outline-primary"
                        onClick={handleResendOtp}
                        disabled={loading || !canResend}
                        size="sm"
                      >
                        {canResend ? (
                          <>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            Renvoyer le code
                          </>
                        ) : (
                          `Renvoyer (${countdown}s)`
                        )}
                      </Button>
                    </div>
                  </div>
                </Form>

                {/* Informations supplémentaires */}
                <div className="mt-4 pt-3 border-top">
                  <div className="text-center small text-muted">
                    <div className="mb-2">
                      <i className="bi bi-info-circle me-2"></i>
                      Le code OTP est valide pendant 5 minutes
                    </div>
                    <div>
                      <i className="bi bi-phone me-2"></i>
                      Vérifiez vos messages SMS sur votre mobile
                    </div>
                    {email && (
                      <div className="mt-2">
                        <i className="bi bi-envelope me-2"></i>
                        {email}
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style>{`
        .form-control:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.1);
        }
        
        input[type="text"] {
          caret-color: transparent;
        }
      `}</style>
    </div>
  );
}